/**
 * What a new property is called, and what a stop is called on a map —
 * `docs/new-request.md` §7.4 (D9, T6) and §3.
 *
 * The name is a suggestion with an **auto** tag: the sheet shows it, follows
 * the type, the company and the contact's name while it is auto, and stops
 * following the moment somebody types over it.
 */
import { segmentOf } from './types.js';
/** The street line without its house number: *412 Orchard Ridge Dr* → *Orchard Ridge Dr*. */
export function streetOf(line1) {
    const words = line1.trim().split(/\s+/);
    if (words.length > 1 && /^\d{1,6}(-?[a-z])?$/i.test(words[0]))
        return words.slice(1).join(' ');
    return line1.trim();
}
/** *Fox Hollow Homeowners Association* and *Fox Hollow HOA* are both *Fox Hollow*. */
function withoutAssociation(company) {
    return company
        .replace(/[,\s]*\b(home\s*owners['’]?\s+association|homeowners['’]?\s+assoc\.?|h\.?o\.?a\.?)\s*$/i, '')
        .trim();
}
const SUFFIX = {
    hoa: 'Common Grounds',
    institutional: 'Campus',
    municipal: 'Grounds',
    industrial: 'Facility',
};
export function propertyName(input) {
    const line1 = input.line1.trim();
    const street = streetOf(line1);
    const type = input.type.trim().toLowerCase();
    if (segmentOf(input.type) === 'residential') {
        const person = [input.firstName, input.lastName].map((part) => part?.trim()).filter(Boolean);
        if (person.length === 0)
            return line1;
        const name = `${person.join(' ')} Residence`;
        return input.contactHasResidence && street ? `${name} · ${street}` : name;
    }
    const company = input.company?.trim();
    if (!company)
        return line1;
    const suffix = SUFFIX[type];
    /* Commercial, and any type this list does not know: the company at the street. */
    if (!suffix)
        return street ? `${company} · ${street}` : company;
    const base = type === 'hoa' ? withoutAssociation(company) || company : company;
    const name = `${base} ${suffix}`;
    const taken = (input.companyPropertyNames ?? [])
        .some((one) => one.trim().toLowerCase() === name.toLowerCase());
    return taken && street ? `${name} · ${street}` : name;
}
/**
 * A stop's short name — the property's name without a trailing *Residence*,
 * *Common Grounds*, *Apartments*, *Campus*, *Grounds* or *Facility*. Used on
 * the map, in a Best time's reason, in the When box and in the bands; blocks
 * in a day and lists keep the full name (§3).
 */
export function stopLabel(name) {
    const trimmed = name.trim();
    const short = trimmed
        .replace(/\s+(Common Grounds|Residence|Apartments|Campus|Grounds|Facility)$/i, '')
        .trim();
    return short || trimmed;
}
//# sourceMappingURL=naming.js.map